/**
 * `@dxf-ai-editor/cad-core` — the block-DEFINITION bounds walk: per-kind
 * child-geometry resolution ({@link childEntityBounds}), the memoize +
 * cycle-guard skeleton shared with `drawing-model`
 * (`resolveDefinitionBounds`) and the STATIC / DYNAMIC regime switch of ONE
 * INSERT ({@link resolveInsertLocalBounds}). The public entry points
 * (`resolveBlockInsertBounds[OrNull]`, MLEADER block content) live in
 * `blockGeometry.ts`, the barrel this module is reached through.
 *
 * This module resolves a block DEFINITION's bounds using cad-core's OWN
 * per-kind leaf math (`./leafBounds.ts`) — NOT
 * `drawing-model/viewportMembership.ts::computeBlockDefinitionBounds`, which
 * was built for a DIFFERENT purpose (the viewport-clip MEMBERSHIP query,
 * where an entity either contributes a coarse AABB or is dropped from the
 * union entirely) and, per that purpose, silently drops several LEGAL block
 * children (`entityModelBounds`'s `default` case returns `null` for
 * Attdef/Dimension/Multileader/Revision Cloud) and only transforms a nested
 * INSERT child by a SINGLE instance (no MINSERT-grid tiling for a nested
 * child) — both real under-reports for a bounds/select/grips query, which
 * must never drop VISIBLE geometry (CLAUDE.md ПРИОРИТЕТ #1; Codex #467 P2).
 * The block-instance INSERT-transform matrix itself (translate → base-point
 * subtract → scale → rotate → translate to the insertion point) IS reused
 * from `viewportMembership.ts::transformBoundsByInsert` (exported for this
 * exact reuse) — that math is unrelated to the membership-query's kind
 * coverage gaps, so re-deriving it here would be a needless duplicate.
 */

import {
  transformBoundsByInsert,
  resolveDefinitionWithCycleGuard,
  blockrefAnnotativeTransforms,
  constantAttdefLocalCorners,
  entityModelBounds,
  safeRecordLookup,
  tableAsBlockReference,
  ALWAYS_INCLUDE,
  type BlockChildEntity,
  type BlockDefinition,
  type BlockInsertEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  attributeInstanceBounds,
  dynamicInstanceEntry,
  extendForMinsertGrid,
  instanceEvaluatedChildren,
  makeMleaderContentInsert,
  type DynamicInstanceEntry,
  type MleaderBlockContentRef,
} from "./blockInsertInstance";
import type { BlockDefinitionBoundsMemo, DimensionStyleTable, DynamicChildrenResolver } from "./blockLookupContext";
import type { Aabb } from "./common";
import { arcBoundsDeg, boundsFromPoints, unionBounds } from "./common";
import { heavyPolylineDrawnBounds } from "./heavyPolylineDrawnBounds";
import { lwpolylineDrawnBounds } from "./lwpolylineDrawnBounds";
import {
  descendContentReferenceFrame,
  descendNestedInsertFrame,
  emitsRecursionPlaceholder,
  recursionPlaceholderLocalBounds,
  type BlockRecursionFrame,
} from "./recursionPlaceholder";
import {
  attdefBounds,
  bulgePolylineBounds,
  dimensionBounds,
  ellipseBounds,
  hatchBounds,
  mtextBounds,
  multileaderBounds,
  splineBounds,
  textBounds,
} from "./leafBounds";

/**
 * Traversal state of ONE definition-bounds resolution, threaded through
 * `childEntityBounds` / `resolveDefinitionBounds` /
 * `resolveInsertLocalBounds` (bundled so the nested-INSERT recursion cannot
 * mis-order the four positional arguments).
 */
export interface DefinitionWalk {
  readonly blocks: Readonly<Record<string, BlockDefinition>>;
  /**
   * The STATIC (instance-agnostic) definition memo —
   * `BlockLookupContext.definitionBoundsMemo` or a fresh per-call map. Also
   * the identity key of the per-instance dynamic side table
   * (`blockInsertInstance.ts::dynamicBoundsByMemo`) and the row-less ATTDEF
   * template table.
   */
  readonly resolved: BlockDefinitionBoundsMemo;
  /**
   * Cycle guard — definition names currently being accumulated, in
   * insertion (= nesting) order, so `Array.from(inProgress)` IS the ancestor
   * chain a dynamic evaluation's own guard expects. Always per-call, never
   * shared.
   */
  readonly inProgress: Set<string>;
  readonly dynamic?: DynamicChildrenResolver;
  /**
   * The document's name-keyed DIMSTYLE table
   * ({@link BlockLookupContext.dimensionStyles}), threaded through the walk
   * EXACTLY like {@link DefinitionWalk.dynamic}: instance-agnostic, constant
   * for the whole traversal, consumed by the `Dimension` arm of
   * {@link childEntityBounds} (Codex PR #784 round 3 P1). Undefined ⇒ a
   * block-child Dimension bounds with AutoCAD's hardcoded DIMVAR defaults,
   * the pre-fix behaviour.
   *
   * NOT part of the `definitionBoundsMemo` cache key — the memo is already
   * documented as valid ONLY for the exact `blockDefinitions` object it was
   * populated against. A caller resolving the SAME `blocks` object against two
   * DIFFERENT style tables through one shared memo must pass a fresh memo, the
   * same rule that already applies to a changed definition graph; the app-side
   * caller (`apps/web/src/app/state/blockExtentsCache.ts`) implements exactly
   * that by keying its memos on the (`blocks`, style-table) PAIR, so a named
   * DIMSTYLE edit — which replaces the style array while leaving `blocks`
   * identical — cannot be served a box computed under the old style (Codex PR
   * #784 round 4 P1).
   */
  readonly dimensionStyles?: DimensionStyleTable;
  /**
   * The accumulated block-reference state of the definition currently being
   * walked (`recursionPlaceholder.ts`) — what the RENDERER's own flatten holds
   * at the same point. Only the recursion PLACEHOLDER needs it (its square is
   * sized by the accumulated scale and clamped in WORLD units, so a purely
   * local box cannot express it), and every descent updates it: a nested
   * INSERT through {@link descendNestedInsertFrame}, an MLEADER block-content
   * reference through {@link descendContentReferenceFrame}.
   *
   * CONTRACT: a walk handed to {@link resolveInsertLocalBounds} must ALREADY
   * carry the frame of THAT insert's definition — every call site descends
   * first (Codex PR #784 round 7 P2).
   */
  readonly frame: BlockRecursionFrame;
  /**
   * Mutable counter of recursion placeholders emitted during THIS traversal —
   * the memo-soundness signal. A definition whose subtree drew a placeholder
   * is FRAME-dependent (the square's world size follows the accumulated
   * scale), so {@link resolveDefinitionBounds} drops such a name from the
   * per-name memo instead of letting a later INSERT with a different scale be
   * served a box computed under the first one's frame. Shared BY REFERENCE
   * with every `{...walk, frame}` copy, so a nested level's increment is
   * visible to the ancestor that must invalidate.
   *
   * Equivalent-mutant note (targeted mutation, PR #784 round 7): the two
   * `count += 1` sites mutated to `-= 1` SURVIVE and are equivalent — the
   * counter is only ever read as a CHANGE DETECTOR (`count !== marks` around a
   * subtree resolution), never as a quantity, so any mutation that still moves
   * the value off its snapshot preserves the observable contract. A mutation
   * that did NOT move it (removing the statement) is killed by the memo tests.
   */
  readonly placeholders: { count: number };
}

/**
 * What {@link resolveInsertLocalBounds} yields for ONE INSERT whose
 * definition EXISTS (a missing name yields `undefined` instead): the
 * definition's block-LOCAL footprint (`ALWAYS_INCLUDE` when it is cyclic /
 * has no usable child geometry), the definition itself and, for a DYNAMIC
 * definition, the per-instance entry the INSERT's ATTRIB rows resolve
 * through ({@link placeInsertInstance}).
 */
export interface InsertLocalResolution {
  readonly bounds: Aabb | typeof ALWAYS_INCLUDE;
  readonly def: BlockDefinition;
  readonly instance: DynamicInstanceEntry | undefined;
}

/**
 * The block-LOCAL footprint of ONE nested block REFERENCE — the single chain
 * every reference a definition can hold goes through, so the three kinds of
 * reference can never disagree about how a referenced definition is resolved,
 * placed or guarded:
 *
 *   - a nested INSERT (`childEntityBounds`'s `Block` arm), descending the
 *     recursion frame by {@link descendNestedInsertFrame};
 *   - a MULTILEADER's BLOCK content ({@link multileaderBlockContentLocalBounds}),
 *     descending by {@link descendContentReferenceFrame} (its own rotation is
 *     an INNER rotation of the enclosing affine);
 *   - an ACAD_TABLE child's anonymous `*T<N>` graphics block
 *     (`childEntityBounds`'s `Table` arm, via the shared
 *     `drawing-model::tableAsBlockReference` projection), which the renderer
 *     composes exactly like MLEADER content — so it descends the same way.
 *
 * A CYCLIC (or depth-capped) reference is not descended by the renderer
 * either — it draws the red `RECURSION:<block>` square + label at this
 * reference's transformed insertion instead (Codex PR #784 round 7 P2).
 * Reserve exactly that footprint: dropping the child (and skipping it) left
 * the placeholder outside the INSERT's stored AABB whenever a resolvable
 * sibling kept the box alive, so cache-direct culling dropped visible geometry
 * and picking missed it.
 *
 * Otherwise the referenced definition's own footprint — its OWN dynamic state
 * when that definition is dynamic — is placed, MINSERT-tiled and union'd with
 * the reference's own `attributes[]` by {@link placeInsertInstance}, exactly
 * like a top-level INSERT.
 *
 * `null` means UNRESOLVABLE (missing definition, or a placeholder whose
 * accumulated frame is singular / non-finite). Each caller maps that onto its
 * own documented conservative answer: `ALWAYS_INCLUDE` for a definition CHILD
 * (never-drop), `null` for MLEADER content (the leader/anchor bounds stand).
 */
function nestedReferenceLocalBounds(
  reference: BlockInsertEntity,
  walk: DefinitionWalk,
  descend: (frame: BlockRecursionFrame, insert: BlockInsertEntity) => BlockRecursionFrame,
): Aabb | null {
  const referenceFrame = descend(walk.frame, reference);
  if (emitsRecursionPlaceholder(reference.blockName, referenceFrame, walk.inProgress)) {
    walk.placeholders.count += 1;
    return recursionPlaceholderLocalBounds(reference.blockName, reference.insertionPoint, walk.frame, referenceFrame);
  }
  return placeInsertInstance(reference, resolveInsertLocalBounds(reference, { ...walk, frame: referenceFrame }), walk.resolved);
}

/**
 * Bounds of a single BLOCK-DEFINITION-CHILD entity, dispatching to cad-core's
 * OWN per-kind leaf math for every legal `BlockChildEntity` member (Autodesk
 * DXF reference + ezdxf `BlockLayout` — the BLOCKS section has the identical
 * entity-structure as ENTITIES, plus ATTDEF; `model.ts`'s
 * `blockChildEntitySchema` comment). `Block` (a nested INSERT) recurses
 * through `resolveInsertLocalBounds` + the SAME insert-transform +
 * MINSERT-grid tiling + per-instance ATTRIB union a top-level INSERT gets
 * ({@link placeInsertInstance}).
 */
function childEntityBounds(child: BlockChildEntity, walk: DefinitionWalk): Aabb | typeof ALWAYS_INCLUDE | null {
  if (child.type === "Block") {
    // THIS nested INSERT's definition-local footprint — its OWN dynamic
    // state when the referenced definition is dynamic — placed, tiled and
    // union'd with its OWN `attributes[]` (baked into THIS definition's
    // authored content) exactly like the top-level INSERT. `ALWAYS_INCLUDE`
    // for an unresolvable (missing / cyclic-with-degenerate-frame) reference:
    // never a silent drop.
    return nestedReferenceLocalBounds(child, walk, descendNestedInsertFrame) ?? ALWAYS_INCLUDE;
  }
  switch (child.type) {
    case "Line":
    case "Point":
    case "Circle": {
      // These kinds' math is already exact + typed in
      // `drawing-model/viewportMembership.ts::entityModelBounds` — reused
      // verbatim (one truth), same as `bounds.ts`'s own top-level default
      // branch.
      const r = entityModelBounds(child);
      return r === ALWAYS_INCLUDE ? ALWAYS_INCLUDE : r;
    }
    case "LWPOLYLINE":
      // Split OUT of that shared arm (@codex PR #785 round 11, F1): an
      // LWPOLYLINE carries the same authored widths a heavy POLYLINE does
      // (the group-43 constant, or the per-vertex 40 / 41 pair that supersedes
      // it) and the canvas draws it as the same mitred band, which
      // width-blind `entityModelBounds` knows nothing about. Leaving this arm
      // behind while `bounds.ts` learned the band would re-create round 9's F2
      // one entity kind later — a top-level ribbon and the identical one
      // nested in a block definition reporting different footprints — so both
      // arms call the ONE function `./lwpolylineDrawnBounds.ts`.
      return lwpolylineDrawnBounds(child);
    case "Text":
      // The rendered glyph box ∪ anchor pair (one-truth
      // `drawing-model::textLocalCorners` via `leafBounds.ts::textBounds`),
      // NOT `entityModelBounds`'s anchor-only pair — a definition's
      // left-justified label runs PAST its anchor, and the INSERT's extent
      // must cover what the renderer draws (Codex PR #784 round 3 P1 "TEXT
      // child extents in definition bounds"). Same case `bounds.ts` applies
      // at the top level.
      return textBounds(child);
    case "Arc":
      // cad-core's OWN full-turn-safe Arc bounds (not the membership helper's
      // `% 360` fold that collapses a full circle — Codex #467 P2), same as
      // `bounds.ts`'s top-level Arc case.
      return arcBoundsDeg(child.center.x, child.center.y, child.radius, child.startAngle, child.endAngle);
    case "Hatch":
      // Exact swept-edge hatch bounds (not the coarse `center ± radius`
      // viewport-cull box — Codex #467 P2), same as `bounds.ts`'s Hatch case.
      return hatchBounds(child);
    case "Ellipse":
      return ellipseBounds(child);
    case "Spline":
      return splineBounds(child);
    case "MText":
      return mtextBounds(child);
    case "Dimension":
      // The child's NAMED DIMSTYLE row (threaded `walk.dimensionStyles`), not
      // only its per-entity `styleOverrides` — a definition whose Dimension
      // child uses a style with a large DIMEXE / DIMTXT / DIMGAP / DIMASZ
      // renders PAST the hardcoded-default box, putting real geometry outside
      // the INSERT metadata bounds (Codex PR #784 round 3 P1).
      return dimensionBounds(child, walk.dimensionStyles);
    case "Multileader": {
      // Leader/anchor/MText-content extent, UNION'd with the referenced BLOCK
      // content footprint (contentType 1) — same treatment `bounds.ts`'s
      // top-level Multileader case applies (Codex #467 P2). Thread the CURRENT
      // walk so a block whose child MLEADER's content points back at THIS
      // definition is detected as a cycle (→ `null`) rather than restarting a
      // fresh traversal that recurses to a stack overflow (Codex #467 P2
      // "Reuse the block cycle guard for MLEADER block content").
      const b = multileaderBounds(child);
      if (child.contentType === 1) {
        const content = multileaderBlockContentLocalBounds(child, walk);
        if (content) return b ? unionBounds(b, content) : content;
      }
      return b;
    }
    case "Revision Cloud":
      return bulgePolylineBounds(child.vertices, child.closed);
    case "Attdef": {
      // Anchor (insertion + alignment point) ∪ a visible CONSTANT ATTDEF's
      // `defaultValue` text box — the renderer draws the constant's value
      // unconditionally (`attdefFromEntity.ts`), so the anchor alone clipped
      // the label (Codex PR #784 round 2 P2). ONE truth: drawing-model's
      // `constantAttdefLocalCorners` (`[]` for an invisible or variable
      // ATTDEF — a variable one is per-instance, `attributeInstanceBounds`).
      const anchor = attdefBounds(child);
      const text = boundsFromPoints(constantAttdefLocalCorners(child));
      if (anchor && text) return unionBounds(anchor, text);
      return anchor ?? text;
    }
    case "Polyline":
      // The DRAWN silhouette (the width band where one is drawn, the
      // tessellated centreline otherwise) swept along the group-39 thickness,
      // union'd with `entityModelBounds`' model box — the very same
      // `heavyPolylineDrawnBounds` call `bounds.ts`' TOP-LEVEL `"Polyline"`
      // arm makes.
      //
      // The pre-fix arm reused width-BLIND `entityModelBounds` here, so a
      // constant-width-4 child on (0,0) → (10,0) contributed a zero-height
      // y ∈ [0, 0] box to its definition while `blockSelectGeometry.ts`'
      // child walk — which goes through `selectLeaf.ts`' `"Polyline"` arm and
      // therefore through the BAND — emitted that same child's select points
      // at y = ±2. `blockSelectGeometry.ts` states the invariant those two
      // sides must keep ("select geometry stays a strict subset of the bounds
      // box, the safe direction") and it was false in the UNSAFE direction:
      // an exact-selection caller using the INSERT box as its coarse
      // candidate filter discards the entity before it ever tests the points
      // that are inside the window (@codex PR #785 round 9, F2).
      //
      // A POLYLINE never yields the never-drop sentinel — `entityModelBounds`'
      // own `"Polyline"` arm is `heavyPolylineBounds`, typed
      // `EntityViewBounds | null`; only the block-reference arms produce
      // `ALWAYS_INCLUDE` — so no sentinel narrowing is needed here (the same
      // documented equivalent mutant `heavyPolylineDrawnBounds` carries).
      return heavyPolylineDrawnBounds(child);
    case "OLE Frame": {
      // The frame rectangle's exact typed math already lives in
      // `drawing-model`'s bounds family (`oleFrameLocal`) and is reused
      // verbatim through the membership helper — one truth, same as the
      // Line/Point/Circle/LWPOLYLINE arm above. (The `"Polyline"` arm shared
      // this branch until round 9; it does not any more, because a heavy
      // POLYLINE's DRAWN shape is wider than its model box and an OLE frame's
      // is not.)
      //
      // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing):
      //   file:line       this file, the `r === ALWAYS_INCLUDE` ternary below
      //   mutator         ConditionalExpression → `false` (i.e. always `r`)
      //   why equivalent  an OLE2FRAME can never BE that sentinel, so the two
      //                   arms of the ternary return the same value
      //   the invariant   `entityModelBounds`' `"OLE Frame"` arm is
      //                   `oleFrameBounds`, typed `EntityViewBounds | null`;
      //                   only the BLOCK-reference arms (`blockInsertBounds`)
      //                   ever produce `ALWAYS_INCLUDE`
      //   proved by       that function's own return type, and
      //                   `./heavyPolylineDrawnBounds.test.ts`'s "leaves an OLE
      //                   Frame child on its own (the arm it used to share)
      //                   unchanged", which pins the real box
      // Kept because the local `r` is typed `ResolvedEntityBounds` and this
      // narrowing is what lets the value flow into an `Aabb | ALWAYS_INCLUDE`
      // return — the same shape the Line/Point/Circle/LWPOLYLINE arm above
      // carries. (Round 8 left this line entirely UNCOVERED; the split gave it
      // a test, which killed its two other mutants.)
      const r = entityModelBounds(child);
      return r === ALWAYS_INCLUDE ? ALWAYS_INCLUDE : r;
    }
    case "Table":
      // A block-child ACAD_TABLE's footprint IS the footprint of the anonymous
      // `*T<N>` block it references (its first subclass is
      // `AcDbBlockReference`, group 2 names that block, 10/20/30 is the
      // insertion point), so it resolves through the very same
      // `resolveInsertLocalBounds` + `placeInsertInstance` chain the
      // nested-INSERT arm above uses — on the ONE shared projection
      // `drawing-model` already defines for the WCS bounds walk
      // (`wcsBoundsBlock.ts::tableWcsBounds`), the viewport-membership index
      // (`viewportMembership.ts::tableBounds`) and cad-core's own TOP-LEVEL
      // Table arm (`bounds.ts`): `tableAsBlockReference`. Never a second "what
      // does a table occupy" derivation, never a fabricated insertion-point
      // box.
      //
      // Returning `null` here (the pre-fix arm, whose comment claimed the
      // nested-INSERT path already covered this — it did not: `childEntityBounds`
      // pre-intercepts only `child.type === "Block"`) made a table inside a
      // block definition contribute NOTHING to that definition's footprint, so
      // an INSERT holding one got a stored AABB that missed the table the
      // renderer draws (`blockChildTable.ts`) — cache-direct culling and
      // picking dropped it (@codex PR #785 round 2).
      //
      // The frame descends as a CONTENT reference (exact composition), not as
      // a nested INSERT (triple fold): the renderer composes the table's
      // `*T<N>` reference onto the enclosing INSERT affine through
      // `composeBlockContentReference` — the same inner-rotation composition a
      // block-child MLEADER's block content goes through — so the recursion
      // placeholder must be sized in exactly that frame.
      //
      // `ALWAYS_INCLUDE` (never `null`) when the `*T<N>` definition is missing
      // or cyclic: the same conservative never-drop fallback the nested-INSERT
      // arm has, so an unresolvable table degrades to the honest
      // insertion-point / include-everything answer instead of vanishing.
      return nestedReferenceLocalBounds(tableAsBlockReference(child), walk, descendContentReferenceFrame) ?? ALWAYS_INCLUDE;
    default: {
      const _never: never = child;
      void _never;
      return null;
    }
  }
}

/**
 * Model-space AABB of a MULTILEADER's BLOCK content (contentType 1) in the
 * frame the MLEADER lives in — the referenced block's footprint placed by the
 * synthetic content INSERT (`makeMleaderContentInsert`), resolved through the
 * CURRENT walk (memo + cycle guard + dynamic hook). The synthetic INSERT
 * carries no per-reference dynamic state, so a dynamic content block
 * evaluates at its authored DEFAULT state — the same regime the renderer's
 * `flattenBlockReference` applies to MLEADER content. Returns `null` when
 * the block is absent / unresolvable / cyclic (conservative — the caller's
 * leader/anchor bounds stand, never shrink). Shared by the block-child
 * Multileader arm above and `blockGeometry.ts::multileaderBlockContentBounds`
 * (the top-level `bounds.ts` case).
 */
export function multileaderBlockContentLocalBounds(e: MleaderBlockContentRef, walk: DefinitionWalk): Aabb | null {
  // Own-property-safe lookup (round 3 P2) — the referenced definition is
  // needed BEFORE the walk, to resolve the MLEADER's `blockAttributeReferences`
  // against its ATTDEFs (`makeMleaderContentInsert`'s doc). `undefined` (a
  // missing name) simply yields a row-less synthetic INSERT; the walk below
  // then returns `undefined` for it anyway.
  const contentInsert = makeMleaderContentInsert(e, safeRecordLookup(walk.blocks, e.blockName));
  // ONE reference chain ({@link nestedReferenceLocalBounds}): the cyclic
  // placeholder reservation (round 7 P2 — a cyclic content block draws the
  // SAME recursion placeholder a cyclic nested INSERT does, because the
  // flatten re-enters through one recursion state) and the
  // `placeInsertInstance` placement (round 9 P2 "Carry MLEADER block
  // attributes into content bounds" — the content block's ATTRIB extent is
  // unioned in exactly as the renderer's flatten draws it, never a bare
  // `transformBoundsByInsert`). The content reference composes EXACTLY onto
  // the enclosing affine — its own rotation is an INNER rotation
  // (`blockChildMultileader.ts::composeBlockContentReference`) — so its frame
  // descends that way. The MINSERT tiling the chain also applies is a no-op
  // here (the synthetic content INSERT is always a 1×1 grid), and it carries
  // no `annotativeContexts`, so the blockref expansion is a single placement.
  return nestedReferenceLocalBounds(contentInsert, walk, descendContentReferenceFrame);
}

/**
 * Union the bounds of a child list (a definition's authored `entities`, or
 * a dynamic instance's EVALUATED children) — the accumulation callback both
 * {@link resolveDefinitionBounds} and the dynamic arm of
 * {@link resolveInsertLocalBounds} hand to the shared cycle guard.
 *
 * An UNRESOLVABLE child (ALWAYS_INCLUDE — a missing/cyclic nested INSERT) or
 * a positionless one (null) is SKIPPED, NOT allowed to collapse the whole
 * definition (Codex #467 P2 "Block bounds lose known-good children when ONE
 * child is unresolvable"): the pre-fix `break → ALWAYS_INCLUDE` discarded
 * every already-accumulated known child, which `resolveBlockInsertBounds`
 * then degraded to the top-level insertion-point fallback — losing visible
 * geometry far from the insertion point. Keep the known extent; fall back to
 * ALWAYS_INCLUDE ONLY when NOTHING at all resolved.
 */
function accumulateChildren(children: readonly BlockChildEntity[], walk: DefinitionWalk): Aabb | typeof ALWAYS_INCLUDE {
  let acc: Aabb | null = null;
  for (const child of children) {
    const cb = childEntityBounds(child, walk);
    if (cb === ALWAYS_INCLUDE || !cb) continue;
    acc = acc ? unionBounds(acc, cb) : cb;
  }
  return acc ?? ALWAYS_INCLUDE;
}

/**
 * Resolve a STATIC block DEFINITION's model-space child-geometry AABB (its
 * authored `entities`), recursively transforming nested INSERT children
 * (incl. their own MINSERT grids and base points) — memoized per name in
 * `walk.resolved` + cycle-safe via the shared `resolveDefinitionWithCycleGuard`
 * (`viewportMembership.ts`, reused rather than re-derived — jscpd flagged the
 * near-identical memoize+cycle-guard skeleton as a duplicate before this
 * reuse), with cad-core's own COMPLETE per-kind coverage as the accumulation
 * callback. Own-property-safe table reads (`safeRecordLookup`, Codex PR #784
 * round 3 P2): an INSERT naming a MISSING block called `constructor` /
 * `toString` / `__proto__` must resolve to "no definition" (ALWAYS_INCLUDE →
 * insertion-point fallback), not treat the inherited `Object.prototype`
 * member as a `BlockDefinition` and throw iterating its `entities`, aborting
 * the whole metadata rebuild.
 *
 * A DYNAMIC definition's per-instance evaluation never goes through here —
 * {@link resolveInsertLocalBounds} routes it to the per-instance side table
 * so this memo stays instance-agnostic.
 */
function resolveDefinitionBounds(name: string, walk: DefinitionWalk): Aabb | typeof ALWAYS_INCLUDE {
  const marks = walk.placeholders.count;
  const result = resolveDefinitionWithCycleGuard(
    name,
    // Stryker disable next-line ConditionalExpression: every caller (`resolveInsertLocalBounds`) has already resolved `name` own-property-safely and returned `undefined` for a missing one, so the predicate is `true` by invariant here; kept as the guard's real contract (a future direct caller must not walk `undefined.entities`), pinned by `blockGeometry.lookup.test.ts`.
    (n) => safeRecordLookup(walk.blocks, n) !== undefined,
    walk.resolved,
    walk.inProgress,
    () => accumulateChildren(safeRecordLookup(walk.blocks, name)!.entities, walk),
  );
  // Memo soundness (Codex PR #784 round 7 P2): a subtree that emitted a
  // recursion placeholder is FRAME-dependent — the square's world size follows
  // the accumulated scale and is clamped at one world unit — so this name must
  // NOT be served from the per-name memo to an INSERT with a different frame.
  // Dropping the entry (rather than keying it) keeps the memo's documented
  // "per name, per `blocks` object" contract intact for every other
  // definition; a cyclic definition simply recomputes, which is bounded work.
  if (walk.placeholders.count !== marks) walk.resolved.delete(name);
  return result;
}

/**
 * The subset of a {@link DefinitionWalk} {@link resolveInsertChildren} reads:
 * the block table, the memo (whose OBJECT identity keys the dynamic
 * per-instance side table), the ancestor-chain cycle guard and the dynamic
 * hook. Narrowed on purpose so the SELECT walk — which carries no recursion
 * frame and no placeholder counter, both bounds-only concerns — can thread
 * the IDENTICAL state through the same resolver (Codex PR #784 round 9 P2).
 */
export type InsertChildrenWalk = Pick<DefinitionWalk, "blocks" | "resolved" | "inProgress" | "dynamic">;

/**
 * What {@link resolveInsertChildren} yields for ONE INSERT whose definition
 * EXISTS: the definition, the per-instance entry for a DYNAMIC definition
 * (`undefined` for a static one — the marker of which regime applies), and a
 * LAZY accessor for the children THIS INSERT actually renders.
 */
export interface InsertChildrenResolution {
  readonly def: BlockDefinition;
  readonly instance: DynamicInstanceEntry | undefined;
  /**
   * The child list the INSERT renders: the definition's AUTHORED `entities`
   * for a static definition, this instance's EVALUATED list for a dynamic one
   * (memoized on the entry — {@link instanceEvaluatedChildren}). LAZY so the
   * bounds walk can keep invoking it strictly INSIDE its cycle guard's
   * accumulate callback: a re-entered definition must never evaluate.
   */
  readonly children: () => readonly BlockChildEntity[];
}

/**
 * The STATIC / DYNAMIC regime switch of ONE INSERT, WITHOUT computing any
 * geometry — the single place that answers "which children does this INSERT
 * render?" (Codex PR #784 round 9 P2 "Evaluate dynamic state for block select
 * geometry").
 *
 * Both walks call it, so they can never disagree:
 *   - {@link resolveInsertLocalBounds} (bounds / grips / metadata AABB), and
 *   - `blockSelectGeometry.ts::insertSelectGeometry` (window / crossing
 *     select points + segments), which previously walked the AUTHORED
 *     `def.entities` unconditionally — so a move / stretch / visibility /
 *     array action returned bounds around the RENDERED instance while the
 *     selection points and segments stayed at the AUTHORED locations (false
 *     positives, missed window/crossing hits).
 *
 * The per-instance entry is keyed by (memo object, ancestor chain,
 * `instanceKey`) — `Array.from(walk.inProgress)` IS that chain, captured
 * BEFORE either walk's guard marks this definition in progress, so the
 * evaluator's own nested-depth / cycle guard sees exactly what the renderer's
 * `resolveEvaluatedChildren` passes it. A missing definition (own-property-
 * safe lookup) is `undefined` — never a throw, never a memo entry, never
 * handed to the dynamic hook.
 */
export function resolveInsertChildren(insert: BlockInsertEntity, walk: InsertChildrenWalk): InsertChildrenResolution | undefined {
  const def = safeRecordLookup(walk.blocks, insert.blockName);
  if (def === undefined) return undefined;
  const dynamic = walk.dynamic?.(insert, def, walk.blocks);
  if (dynamic === undefined) return { def, instance: undefined, children: () => def.entities };
  const parentChain = Array.from(walk.inProgress);
  const instance = dynamicInstanceEntry(walk.resolved, parentChain, dynamic.instanceKey);
  return { def, instance, children: () => instanceEvaluatedChildren(instance, dynamic, parentChain) };
}

/**
 * The block-LOCAL footprint of the definition THIS INSERT references — the
 * one place that decides between the two regimes (Codex PR #784 round 3 P1):
 *
 *   - STATIC definition (no `walk.dynamic` hook, or the hook returns
 *     `undefined`) → {@link resolveDefinitionBounds}: the authored
 *     `entities`, memoized per name (instance-agnostic).
 *   - DYNAMIC definition (the hook returns a `DynamicInstanceChildren`) → the
 *     EVALUATED children for THIS INSERT's `customProperties` /
 *     `visibilityState`, memoized per (memo, ancestor chain, instanceKey) in
 *     the per-instance side table (`blockInsertInstance.ts::
 *     dynamicInstanceEntry`). The evaluation itself is lazy — the `children`
 *     thunk runs only on a memo miss — and receives the ancestor chain
 *     `Array.from(walk.inProgress)` captured BEFORE the guard marks this
 *     definition in progress (so the evaluator's own cycle guard does not see
 *     the definition as its own ancestor); the evaluated list is RETAINED on
 *     the entry so the INSERT's ATTRIB rows resolve against the evaluated
 *     ATTDEF anchors. Nested `Block` children of the evaluated list recurse
 *     through `childEntityBounds` → this function again with the CHILD's own
 *     state, exactly as the renderer re-invokes `evaluateBlock` per nesting
 *     level; nested STATIC children still hit the static memo.
 *
 * Both regimes return a box in the definition's LOCAL frame — the caller
 * ({@link placeInsertInstance}) applies the INSERT affine
 * (`transformBoundsByInsert`) + MINSERT tiling + the per-instance ATTRIB
 * union, so a dynamic instance is placed by the very same chain a static one
 * is. A missing definition (own-property-safe lookup) is `undefined` — never
 * a throw, never a memo entry, never handed to the dynamic hook.
 */
export function resolveInsertLocalBounds(insert: BlockInsertEntity, walk: DefinitionWalk): InsertLocalResolution | undefined {
  const resolution = resolveInsertChildren(insert, walk);
  if (resolution === undefined) return undefined;
  const { def, instance } = resolution;
  if (instance === undefined) {
    return { bounds: resolveDefinitionBounds(insert.blockName, walk), def, instance: undefined };
  }
  const marks = walk.placeholders.count;
  // `resolution.children()` stays INSIDE the guard callback: a re-entered
  // (cyclic) definition must never reach the evaluator at all, exactly as
  // before the shared resolver was extracted.
  const bounds = resolveDefinitionWithCycleGuard(insert.blockName, () => true, instance.resolved, walk.inProgress, () =>
    accumulateChildren(resolution.children(), walk),
  );
  // Same frame-dependence rule the STATIC memo above applies — a dynamic
  // instance whose evaluated children drew a recursion placeholder cannot be
  // replayed for a differently-scaled INSERT of the same (chain, state):
  // the side table is keyed by the dynamic STATE, which carries no scale
  // (`blockGeometry.dynamic.test.ts` — "a cyclic evaluated child is NOT
  // memoized under one INSERT's scale").
  if (walk.placeholders.count !== marks) instance.resolved.delete(insert.blockName);
  return { bounds, def, instance };
}

/**
 * Place ONE INSERT's resolved definition footprint into the frame the INSERT
 * lives in: the INSERT affine (`transformBoundsByInsert` — base point
 * subtract → scale → rotate → translate), tiled across its MINSERT grid,
 * UNION'd with THIS instance's own ATTRIB extent (per-instance values /
 * EATTEDIT placement — never memoized; `attributeInstanceBounds`' doc). The
 * ONE chain both the top-level INSERT (`blockGeometry.ts`) and a nested
 * `Block` child (`childEntityBounds`) go through.
 *
 * ANNOTATIVE placement (Codex PR #784 round 9 P2 "Include nested blockref
 * contexts in INSERT bounds"): the whole chain runs ONCE PER PLACEMENT — the
 * master INSERT plus one synthetic INSERT per matched `kind: "blockref"`
 * annotative representation ({@link blockrefAnnotativeTransforms}, imported
 * from `drawing-model` rather than re-derived: it is the same expansion
 * `blockInsertWcsBounds` and its nested-`Block` arm already use, and it
 * mirrors the renderer's own `resolveTypedAnnotation(insert.
 * annotativeContexts, ctx)` override of insertion / scaleX / scaleY /
 * rotation, applied by `blockFromEntity.ts` at the TOP-LEVEL entry AND in the
 * nested-INSERT arm `flattenTypedInsert` recurses through). Placing only the
 * master left an annotative INSERT — top-level or nested — whose active
 * context re-places / re-scales / re-rotates it OUTSIDE the metadata AABB, so
 * cache-direct viewport culling, fit-to-extents and broad-phase selection
 * lost the geometry the renderer actually draws.
 *
 * Only the PLACEMENT is per-context: the definition-LOCAL footprint (`r`) is
 * placement-invariant by construction — a blockref context substitutes
 * insertion/scale/rotation only, never the referenced `blockName` nor the
 * `customProperties`/`visibilityState` that pick a dynamic instance — so one
 * resolution feeds every placement (and the memo stays instance-agnostic).
 * MINSERT tiling and the per-instance ATTRIB extent ride each placement's own
 * scale/rotation, the same round-25 "Fix 5" rule `blockInsertWcsBounds`
 * applies (grid COUNTS and SPACINGS are copied verbatim by the expansion, so
 * only the per-cell world delta differs) — which is why
 * {@link attributeInstanceBounds} is called per placement rather than once:
 * it OWNS that affine + tiling internally. The repeated ATTRIB resolution is
 * bounded by the (small) representation count, and the overwhelmingly common
 * row-less INSERT hits the per-(memo, name) template memo on every placement
 * after the first, so the memo contract "a memo hit never re-walks the
 * definition" is untouched. A NON-annotative INSERT — every INSERT in a
 * drawing without annotation scales — runs exactly one iteration, byte-identical
 * to the pre-fix single placement.
 *
 * `null` when the definition is unresolvable (`undefined` — missing — or an
 * `ALWAYS_INCLUDE` footprint: no partial attribute-only box for a missing /
 * cyclic block — the renderer draws nothing for an unknown block, so the
 * honest insertion-point fallback stands, same rule `blockInsertWcsBounds`
 * applies) or EVERY placement's box is non-finite.
 */
export function placeInsertInstance(
  insert: BlockInsertEntity,
  r: InsertLocalResolution | undefined,
  memo: BlockDefinitionBoundsMemo,
): Aabb | null {
  // Equivalent-mutant note (targeted mutation, PR #784): the
  // `r.bounds === ALWAYS_INCLUDE` operand mutated to `false` SURVIVES and is
  // mathematically equivalent — the sentinel is a STRING, so
  // `transformBoundsByInsert` reads `undefined` corners, produces NaN and
  // returns `null` through its own finite guard: the observable result is the
  // same `null`. Deliberately NOT suppressed: `Stryker disable next-line
  // ConditionalExpression` is per-MUTATOR, so it would also hide the KILLABLE
  // `-> true` twin on this line. Same equivalence previously triaged at the
  // pre-split `blockGeometry.ts` site.
  if (r === undefined || r.bounds === ALWAYS_INCLUDE) return null;
  let out: Aabb | null = null;
  for (const placement of [insert, ...blockrefAnnotativeTransforms(insert)]) {
    const single = transformBoundsByInsert(r.bounds, placement, r.def.basePoint);
    if (!single) continue;
    const tiled = extendForMinsertGrid(single, placement);
    const attr = attributeInstanceBounds(placement, r.def, memo, r.instance);
    const placed = attr ? unionBounds(tiled, attr) : tiled;
    out = out ? unionBounds(out, placed) : placed;
  }
  return out;
}
